import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { vibrate } from '../utils/haptics';
import { motion, AnimatePresence } from 'framer-motion';
import FloatingBackButton from './FloatingBackButton';
import { getWordTranslation } from '../utils/localizedContent';
import { EmojiText } from './common/Picto';
import './SummaryScreen.css';

export interface SummaryStat {
  value: string;
  label: string;
}

export interface SummaryScreenProps {
  theme: 'light' | 'dark';
  label: string;
  congrats: { text: string; translation: string; translationEn?: string; illustration?: string };
  stats: [SummaryStat, SummaryStat, SummaryStat];
  proverb?: { text: string; translation: string; translationEn?: string; illustration?: string };
  children?: React.ReactNode;
  primaryBtn: { label: string; onClick: () => void };
  secondaryBtn?: { label: string; onClick: () => void };
  extraLink?: { label: string; onClick: () => void };
  onBack?: () => void;
  onShare?: () => void;
}

const SummaryScreen: React.FC<SummaryScreenProps> = ({
  theme,
  label,
  congrats,
  stats,
  proverb,
  children,
  primaryBtn,
  secondaryBtn,
  extraLink,
  onBack,
  onShare,
}) => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(0);
  const hasDetail = Boolean(children);

  useEffect(() => {
    vibrate('summary');
  }, []);

  const CTA = (
    <div className="summ-cta">
      <motion.button
        className="summ-cta__btn"
        onClick={primaryBtn.onClick}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.97 }}
      >
        <EmojiText text={primaryBtn.label} />
      </motion.button>
      {secondaryBtn && (
        <motion.button
          className="summ-cta__secondary"
          onClick={secondaryBtn.onClick}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
        >
          <EmojiText text={secondaryBtn.label} />
        </motion.button>
      )}
      {extraLink && (
        <button className="summ-cta__link" onClick={extraLink.onClick}>
          {extraLink.label} ›
        </button>
      )}
    </div>
  );

  return (
    <div className="summ-screen" data-theme={theme}>
      {onBack && page === 0 && (
        <FloatingBackButton onBack={onBack} position="top-left" variant="minimal" />
      )}
      {page === 1 && (
        <FloatingBackButton onBack={() => setPage(0)} position="top-left" variant="minimal" />
      )}

      <AnimatePresence>
        {page === 0 ? (
          <motion.div
            key="page0"
            className="summ-page"
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.2 }}
          >
            {/* ── Arch ─────────────────────────────────────────── */}
            <motion.div
              className="summ-arch"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="summ-arch__pattern summ-arch__pattern--top" aria-hidden="true" />
              <p className="summ-arch__label">{label}</p>
              {congrats.illustration && (
                <p className="summ-arch__glyphs">{congrats.illustration}</p>
              )}
              <h1 className={`summ-arch__title${congrats.illustration ? ' summ-arch__title--sub' : ''}`}>
                {congrats.text}
              </h1>
              {congrats.translation && (
                <p className="summ-arch__translation">{getWordTranslation(congrats, i18n.language)}</p>
              )}
              <div className="summ-arch__stats">
                {stats.map((s, i) => (
                  <div key={i} className="summ-stat">
                    <span className="summ-stat__value">{s.value}</span>
                    <span className="summ-stat__label">{s.label}</span>
                  </div>
                ))}
              </div>
              {onShare && (
                <button className="summ-arch__share" onClick={onShare}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="16" height="16">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/>
                  </svg>
                  Partager ma progression
                </button>
              )}
              <div className="summ-arch__pattern summ-arch__pattern--bottom" aria-hidden="true" />
            </motion.div>

            {/* ── Proverb card ──────────────────────────────────── */}
            {proverb && (
              <motion.div
                className="summ-proverb"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <p className="summ-proverb__dots" aria-hidden="true">; ; ; ; ; ;</p>
                <p className="summ-proverb__label">{t('revision.proverbOfDay')}</p>
                {proverb.illustration && (
                  <p className="summ-proverb__glyphs">{proverb.illustration}</p>
                )}
                <p className="summ-proverb__text">« {proverb.text} »</p>
                <p className="summ-proverb__translation">{getWordTranslation(proverb, i18n.language)}</p>
              </motion.div>
            )}

            {/* ── CTA page 0 ───────────────────────────────────── */}
            <div className="summ-cta">
              <motion.button
                className="summ-cta__btn"
                onClick={hasDetail ? () => setPage(1) : primaryBtn.onClick}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <EmojiText text={primaryBtn.label} />
              </motion.button>
              {secondaryBtn && (
                <motion.button
                  className="summ-cta__secondary"
                  onClick={secondaryBtn.onClick}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <EmojiText text={secondaryBtn.label} />
                </motion.button>
              )}
              {hasDetail && (
                <button className="summ-cta__link" onClick={() => setPage(1)}>
                  Voir le détail ›
                </button>
              )}
              {!hasDetail && extraLink && (
                <button className="summ-cta__link" onClick={extraLink.onClick}>
                  {extraLink.label} ›
                </button>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="page1"
            className="summ-page"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.2 }}
          >
            {/* ── Word list ────────────────────────────────────── */}
            <div className="summ-content">
              {children}
            </div>

            {/* ── CTA page 1 ───────────────────────────────────── */}
            {CTA}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SummaryScreen;
